import { isSlideQuestionSlide, Slide, SlideType } from '@llx/common';
import { PatientCaseContent } from '@llx/common';
import { useEffect, useMemo } from 'react';

import {
  ContextType,
  HostFieldsFieldsFragment,
  MeetingFieldsFragment,
} from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import MeetingQuestionContextProvider from '@/components/CaseSlide/QuestionSlide/context/MeetingQuestionContext';
import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import useMeetingTrackingAssetStats from '@/features/tracking/useMeetingTrackingAssetStats';
import { useAnswersBySlide } from '@/hooks/useAnswersBySlide';

import CaseExplorerView from './CaseExplorerView';
import { useCurrentSlideData } from './currentSlideData.store';

export type CaseExplorerClientProps = {
  patientCaseIndex: number;
  patientCase: PatientCaseContent;
  isHost: boolean;

  meeting?: MeetingFieldsFragment;
  hostFields?: HostFieldsFieldsFragment;
  forcedCurrSlide?: Slide | undefined;
  trackAssetSwitch?: ReturnType<typeof useMeetingTrackingAssetStats>['trackAssetSwitch'];
};

export default function CaseExplorerClient(props: CaseExplorerClientProps) {
  const { userID } = useLoggedInUserAuthContext();
  const {
    currentMeeting,
    currentCase,
    currentPatientCaseId,
    slideVariationDetails,
    currentHostAssetID,
  } = useCurrentMeetingDataContext();
  const isPreview = !!props.forcedCurrSlide;
  const slideStore = useCurrentSlideData();

  const slideIndex = useMemo(() => {
    if (isPreview && props.forcedCurrSlide) {
      return props.patientCase.slides.indexOf(props.forcedCurrSlide);
    }
    return props.hostFields?.currentSlide ?? 0;
  }, [isPreview, props.forcedCurrSlide, props.hostFields]);

  const currentSlide: Slide | undefined =
    props.forcedCurrSlide || props.patientCase?.slides?.[slideIndex];

  const answersForSlideDetails = useAnswersBySlide({
    contextType: ContextType.Meeting,
    objId: currentMeeting.id,
    slideId: currentSlide.id,
    isPreview: isPreview,
    patientCaseContent: currentCase,
    patientCaseId: currentPatientCaseId,
  });

  useEffect(() => {
    if (currentSlide) {
      slideStore.setCurrentSlide(currentSlide);
      slideStore.setCurrentSlideWithVariation(currentSlide);
    }
    if (
      !slideVariationDetails.isLoading &&
      slideVariationDetails.slideHasVariations &&
      slideVariationDetails.slideVariation
    ) {
      slideStore.setCurrentSlideWithVariation(slideVariationDetails.slideVariation.variation);
    }
  }, [currentSlide, slideVariationDetails]);

  if (!currentCase) {
    throw new Error('CaseExplorerClient is used without having a currentCase');
  }

  const caseExplorerViewJSX = (
    <CaseExplorerView
      {...props}
      currentPatientCaseId={currentPatientCaseId}
      currSlideVariationDetails={slideVariationDetails}
      isPreview={isPreview}
      currentSlide={currentSlide}
      currentSlideWithVariation={slideStore.currentSlideWithVariation}
      slideIndex={slideIndex}
      userID={userID}
      currentHostAssetID={currentHostAssetID}
    />
  );

  if (isSlideQuestionSlide(currentSlide) || currentSlide.type === SlideType.CUSTOM) {
    return (
      <MeetingQuestionContextProvider
        slide={currentSlide}
        meeting={props.meeting}
        currentCase={currentCase}
        currentPatientCaseId={currentPatientCaseId}
        isUserHost={props.isHost}
        answersForSlideDetails={answersForSlideDetails}
      >
        {caseExplorerViewJSX}
      </MeetingQuestionContextProvider>
    );
  }

  return caseExplorerViewJSX;
}
